import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 touch-target active:scale-95",
  {
    variants: {
      variant: {
        default: "bg-terracotta text-terracotta-foreground hover:bg-terracotta/90 active:bg-terracotta/95 shadow-sm hover:shadow-md tracking-wide",
        accent:
          "bg-terracotta text-terracotta-foreground hover:bg-terracotta/90 active:bg-terracotta/95 shadow-sm hover:shadow-md tracking-wide",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/95",
        outline:
          "border border-terracotta text-terracotta bg-transparent hover:bg-terracotta hover:text-terracotta-foreground tracking-wide",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/70",
        ghost: "hover:bg-secondary hover:text-secondary-foreground active:bg-secondary/80",
        link: "text-terracotta underline-offset-4 hover:underline active:text-terracotta/80",
        luxury: "bg-foreground text-background hover:bg-foreground/90 tracking-[0.1em] font-medium active:bg-foreground/95",
        premium: "bg-foreground text-background hover:bg-foreground/90 tracking-[0.1em] font-medium active:bg-foreground/95",
        hero: "bg-transparent border border-white text-white hover:bg-white hover:text-foreground tracking-[0.1em] active:bg-white/90",
        kering: "bg-transparent border border-foreground text-foreground hover:bg-foreground hover:text-background tracking-[0.1em] font-medium uppercase text-xs active:bg-foreground/90",
        minimal: "bg-background border border-border text-foreground hover:bg-muted tracking-wide active:bg-muted/80",
        mobile: "h-12 text-base font-medium bg-terracotta text-terracotta-foreground hover:bg-terracotta/90 active:bg-terracotta/95",
        terracotta: "bg-terracotta text-terracotta-foreground hover:bg-terracotta/90 active:bg-terracotta/95 shadow-sm hover:shadow-md tracking-wide",
        "terracotta-outline": "border border-terracotta text-terracotta bg-transparent hover:bg-terracotta hover:text-terracotta-foreground tracking-wide",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        xl: "h-14 px-10 text-base",
        icon: "h-10 w-10",
        "mobile-icon": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
